import { AxiosError, AxiosResponse } from 'axios';
import { useQuery } from 'react-query';

import {
  AgencyFilterDto,
  InvalidationRuleDto,
  ListInvalidationRuleDto,
  PagedResponse
} from '@bandido/shared/dtos';

import { QueryKeys } from '../common/types';
import { client } from '../common/services';
import {CREATE_INVALIDATION_RULE} from '@frontend/features/invalidation-rules/constants';


interface useGetInvalidationRulesByAgencyProps extends Omit<AgencyFilterDto, 'agencyId'> {
  onErrorCallback?: (_e: AxiosError) => void;
  agencyId: number;
}


export const useGetInvalidationRulesByAgency = (params?: useGetInvalidationRulesByAgencyProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, agencyId } = params || {};

  return useQuery({
    queryKey: [QueryKeys.getOrganizationsByAgencyId, currentPage, pageSize, search, agencyId],
    queryFn: async (): Promise<PagedResponse<ListInvalidationRuleDto> | undefined> => {
      try {
        const result: AxiosResponse<PagedResponse<ListInvalidationRuleDto>, any> = await client.get(
          `invalidation-rules/${agencyId}/list`,
          {
            params: {
              currentPage,
              pageSize,
              search,
            },
          },
        );

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(agencyId),
  });
};

interface UseGetInvalidationRulesById {
  id: string;
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetInvalidationRuleById = ({ id, onErrorCallback }: UseGetInvalidationRulesById) =>
  useQuery({
    queryKey: [QueryKeys.getInvalidationRuleById, id],
    queryFn: async (): Promise<InvalidationRuleDto | undefined> => {
      try {
        const result: AxiosResponse<InvalidationRuleDto, any> = await client.get(`invalidation-rules/${id}`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id) && id!==CREATE_INVALIDATION_RULE,
  });
